import {ChangeDetectionStrategy, Component, computed, inject, input, linkedSignal, output, signal} from '@angular/core';
import {Moment} from 'moment';
import {DateFormatService} from '../date-format/date-format.service';
import {nowInTimezone} from '../timezone';
import {DatePickerView, DayCell, MonthCell, YearCell} from './date-picker';
import {
  DAYS_PER_WEEK,
  MONTHS_SHORT,
  WEEKDAYS,
  WEEKS_SHOWN,
  YEAR_FORMAT,
  YEARS_PER_PAGE,
} from './date-picker.constants';

/**
 * Date picker, Monday first. Own implementation rather than a native `input[type=date]` popup:
 * the native ones cannot be sized or formatted to the design, and differ per engine.
 *
 * Clicking the header opens the year grid (a {@link YEARS_PER_PAGE}-year page); picking a year
 * drops to that year's months, and picking a month drops to that month's days — so a far-off date
 * is year → month → day rather than a long press on the arrows. The arrows step whichever unit the
 * current view shows.
 */
@Component({
  selector: 'app-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrl: './date-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {class: 'app-date-picker'},
})
export class DatePickerComponent {
  readonly value = input<Moment | null>(null);
  /** Earliest date the user may pick, if any. */
  readonly min = input<Moment | null>(null);
  /** Latest date the user may pick, if any. */
  readonly max = input<Moment | null>(null);
  /**
   * IANA zone whose wall clock decides "today": the ring and the fallback month. A zone rather
   * than a `today: Moment` input because today is derived fresh inside each computed, so it stays
   * right across midnight while a panel is open — a precomputed moment would go stale. `null`
   * (the default) is the selected company's zone, and the browser clock only without a single one.
   * Cells stay plain wall-clock moments either way.
   */
  readonly timezone = input<string | null>(null);
  readonly dateSelect = output<Moment>();

  private readonly formats = inject(DateFormatService);
  /** {@link timezone}, else the selected company's. */
  private readonly effectiveTimezone = computed(() => this.timezone() ?? this.formats.timezone());

  protected readonly weekdays = WEEKDAYS;

  /** Which grid is on screen; the header cycles it up, a selection drops it back down. */
  protected readonly view = signal<DatePickerView>('days');

  /** The month on screen. Follows `value`, but the header/arrows move it on their own. */
  private readonly viewMonth = linkedSignal<Moment | null, Moment>({
    source: this.value,
    computation: (value, previous) =>
      value?.clone().startOf('month') ?? previous?.value ?? nowInTimezone(this.effectiveTimezone()).startOf('month'),
  });

  protected readonly headerLabel = computed(() => {
    const month = this.viewMonth();
    switch (this.view()) {
      case 'days': return month.format(this.formats.pattern('monthYear'));
      case 'months': return month.format(YEAR_FORMAT);
      case 'years': { const start = this.yearPageStart(); return `${start}–${start + YEARS_PER_PAGE - 1}`; }
    }
  });

  protected readonly weeks = computed<DayCell[][]>(() => {
    const month = this.viewMonth();
    const selected = this.value();
    const min = this.min();
    const max = this.max();
    const today = nowInTimezone(this.effectiveTimezone()).startOf('day');
    const cursor = month.clone().startOf('isoWeek');
    return Array.from({length: WEEKS_SHOWN}, () => Array.from({length: DAYS_PER_WEEK}, () => {
      const date = cursor.clone();
      cursor.add(1, 'day');
      return {
        date,
        label: date.date(),
        outside: !date.isSame(month, 'month'),
        today: date.isSame(today, 'day'),
        selected: !!selected && date.isSame(selected, 'day'),
        weekend: date.isoWeekday() > 5,
        disabled: (!!max && date.isAfter(max, 'day')) || (!!min && date.isBefore(min, 'day')),
      };
    }));
  });

  protected readonly months = computed<MonthCell[]>(() => {
    const month = this.viewMonth();
    const selected = this.value();
    const min = this.min();
    const max = this.max();
    const today = nowInTimezone(this.effectiveTimezone());
    return MONTHS_SHORT.map((label, index) => {
      const date = month.clone().month(index).startOf('month');
      return {
        date,
        label,
        today: date.isSame(today, 'month'),
        selected: !!selected && date.isSame(selected, 'month'),
        disabled: (!!max && date.isAfter(max, 'month')) || (!!min && date.isBefore(min, 'month')),
      };
    });
  });

  protected readonly years = computed<YearCell[]>(() => {
    const start = this.yearPageStart();
    const template = this.viewMonth();
    const selected = this.value();
    const min = this.min();
    const max = this.max();
    const today = nowInTimezone(this.effectiveTimezone());
    return Array.from({length: YEARS_PER_PAGE}, (_, index) => {
      const date = template.clone().year(start + index).startOf('year');
      return {
        date,
        label: start + index,
        today: date.isSame(today, 'year'),
        selected: !!selected && date.isSame(selected, 'year'),
        disabled: (!!max && date.isAfter(max, 'year')) || (!!min && date.isBefore(min, 'year')),
      };
    });
  });

  /** First year of the page containing the view — aligned so paging never straddles a boundary. */
  private yearPageStart(): number {
    const year = this.viewMonth().year();
    return year - (year % YEARS_PER_PAGE);
  }

  /** The header opens year selection; from there a pick drops to months, then to days. */
  protected openYears(): void {
    this.view.set('years');
  }

  /** The arrows step whichever unit the current view shows: a month, a year, or a year page. */
  protected step(direction: number): void {
    this.viewMonth.update(month => {
      switch (this.view()) {
        case 'days': return month.clone().add(direction, 'month');
        case 'months': return month.clone().add(direction, 'year');
        case 'years': return month.clone().add(direction * YEARS_PER_PAGE, 'year');
      }
    });
  }

  protected selectMonth(cell: MonthCell): void {
    this.viewMonth.set(cell.date.clone());
    this.view.set('days');
  }

  protected selectYear(cell: YearCell): void {
    this.viewMonth.update(month => month.clone().year(cell.date.year()).startOf('month'));
    this.view.set('months');
  }

  protected select(day: DayCell): void {
    this.dateSelect.emit(day.date.clone());
  }
}
